import { useEffect, useState } from "react";

import { Popover } from "antd";
import { TooltipPlacement } from "antd/lib/tooltip";
import { IS_FORECAST_TRUMPING_DEFAULT_CHART } from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import {
  FORECAST_TRUMPING_FOLDER_ID,
  FORECAST_TRUMPING_FOLDER_NAME,
  MCDANIEL_SEARCH_FOLDER_NAME
} from "constants/settings.constants";

import { IGroupBy, IGroupByMap, PerformanceDateRange } from "models";
import { EntityKind } from "models/entityKind";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

import { useGeomBinContext } from "components/geom-bin/hooks/useGeomBinContext";

import { GroupByStoreProvider } from "./GroupByStoreProvider";
import GroupByWrapper from "./GroupByWrapper";

export type GroupByValueT = IGroupBy | IGroupByMap;

export type GroupByT = {
  children: React.ReactNode;
  dataTypeFilters?: string[];
  excludedCategoryFilters?: string[];
  editable?: boolean;
  placement?: TooltipPlacement;
  value: GroupByValueT;
  chartType?: string;
  axisType?: string;
  entityKind?: EntityKind;
  isDisabled?: boolean;
  hideDateRange?: boolean;
  /**
   * Hides the forecast toggle and the production/forecast source selects, for a caller that
   * keeps only the picked property and fetches it with settings of its own.
   */
  hideSourceControls?: boolean;
  /**
   * The category the picker opens on while no field is chosen yet. Ignored when the
   * entity's catalogue has no such category.
   */
  defaultCategoryName?: string;
  onChange: (
    value: IGroupBy,
    isForecastToggleOn: boolean,
    selectedForecastFolder: string,
    selectedForecastFolderName: string,
    selectedPdenSource: PdenSourceEnum,
    performanceDateRange?: PerformanceDateRange
  ) => void;
  /**
   * Whether this picker is the one driving the main surface — the focus
   * flyouts set it, every other consumer (ML pickers, normalize-by, XDA, chart
   * axes) leaves it off. Picking a shapefile only zooms the map when it is set.
   */
  ownsFocus?: boolean;
  /**
   * Whether this picker may offer the shapefile filtering toggle. Only the map
   * focus flyout sets it, because the toggle writes the global well filter
   * rather than anything belonging to the picker: a chart's own focus flyout
   * owns a focus but not that filter, so it leaves this off.
   */
  allowsShapefileFilter?: boolean;
  /**
   * The production source this picker opens on, for a caller that keeps one of its own.
   * Only the focus tiles in user settings pass it: each tile stores its own source, and
   * the committed focus names the tab's rather than the tile's. Left off, the picker opens
   * the way it always has.
   */
  pinnedPdenSource?: number;
  chartTypeParam?: Partial<IGroupBy>;
  /**
   * External signal to close this picker's popover, for a caller that hides
   * its trigger via CSS rather than unmounting it — the popover content is
   * portaled outside the trigger's DOM subtree, so an invisible trigger
   * wouldn't otherwise close it. Mirrors the isGeomBinOpen guard below.
   */
  forceClose?: boolean;
};

export default function GroupBy({
  children,
  dataTypeFilters = [],
  excludedCategoryFilters = [],
  editable = true,
  placement = "right",
  value = null,
  chartType = null,
  axisType = null,
  entityKind = EntityKind.Well,
  isDisabled = false,
  hideDateRange = false,
  hideSourceControls = false,
  defaultCategoryName,
  onChange,
  ownsFocus = false,
  allowsShapefileFilter = false,
  pinnedPdenSource = null,
  chartTypeParam = null,
  forceClose = false
}: GroupByT) {
  const [visible, setVisible] = useState(false);
  const { isActive: isGeomBinOpen } = useGeomBinContext();

  const handleChange = (
    newValue,
    isForecastToggleOn,
    selectedForecastFolder,
    selectedForecastFolderName,
    selectedPdenSource,
    performanceDateRange?: PerformanceDateRange
  ) => {
    onChange(
      newValue,
      isForecastToggleOn,
      selectedForecastFolder,
      selectedForecastFolderName,
      selectedPdenSource,
      performanceDateRange
    );
    setVisible(false);
  };

  const handleVisibleChange = (val: boolean) => {
    if (!isDisabled) {
      setVisible(val);
    }
  };

  useEffect(() => {
    if (visible && (isGeomBinOpen || forceClose)) {
      setVisible(false);
    }
  }, [isGeomBinOpen, forceClose]);

  const isTrumpingDefault = IS_FORECAST_TRUMPING_DEFAULT_CHART(chartType as EvaChart);

  const content = (
    <GroupByStoreProvider
      overrides={{
        entityKind,
        isForecastToggleOn: entityKind === EntityKind.Well,
        selectedForecastFolder: isTrumpingDefault
          ? FORECAST_TRUMPING_FOLDER_ID
          : MCDANIEL_SEARCH_FOLDER_NAME,
        selectedForecastFolderName: isTrumpingDefault
          ? FORECAST_TRUMPING_FOLDER_NAME
          : MCDANIEL_SEARCH_FOLDER_NAME
      }}>
      <GroupByWrapper
        value={value}
        dataTypeFilters={dataTypeFilters}
        excludedCategoryFilters={excludedCategoryFilters}
        onChange={handleChange}
        ownsFocus={ownsFocus}
        allowsShapefileFilter={allowsShapefileFilter}
        pinnedPdenSource={pinnedPdenSource}
        editable={editable}
        chartType={chartType}
        axisType={axisType}
        entityKind={entityKind}
        chartTypeParam={chartTypeParam}
        isVisible={visible}
        hideDateRange={hideDateRange}
        hideSourceControls={hideSourceControls}
        defaultCategoryName={defaultCategoryName}
      />
    </GroupByStoreProvider>
  );

  return (
    <Popover
      content={content}
      placement={placement}
      trigger="click"
      open={!isDisabled && visible}
      onOpenChange={handleVisibleChange}
      overlayClassName="group-by-popover">
      {children}
    </Popover>
  );
}
